import { ApiError } from '@/lib/api';
import { pinResolvedWord } from '@/lib/references';
import { versionedDay } from '@/lib/versioned-labels';
import type {
  ConversationIneligibleReason,
  ExperienceCard,
  ExperienceLinkFilter,
  ExperienceProblem,
  ExperienceProductionBlocker,
  ExperienceRetiredReference,
  ExperienceVariable,
  ExperienceWarning,
} from '@matter_extractor/shared';
import { analystLevelsPhrase, assertNever, experienceErrorSchema } from '@matter_extractor/shared';

/**
 * Everything the Expériences screens SAY that the versioned layer cannot say
 * for them (refonte modulaire §9.1, §10.2, §15).
 *
 * Pure, and written before the three screens that read it, for the reason
 * `protocol-labels.ts` gives at its own head: a condition worded twice is
 * worded two ways, and the half that drifts is always the one nobody reads.
 * The line of facets is read by the card AND by the capture script's
 * expectations; a refusal is worded by the save box AND by a « Passer en
 * production » fired from the history.
 *
 * ## Every refusal is worded from its `code` and its `kind`
 *
 * `experienceErrorSchema` carries a closed catalogue of two codes and, behind
 * each, a list whose entries carry their own `kind` (§9.1). The server's
 * `message` is an English developer string bound for the logs and the OpenAPI;
 * it never reaches an operator. That is the same doctrine as
 * `versioned-labels.ts` and `protocol-labels.ts`, and it is what lets the save
 * box word a problem in its red encart and the pointer box word the very same
 * fact under « Passer en production ».
 */

/** « 1 page » / « 5 pages » - French plural on a count. */
export function countLabel(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/* ---------------------------------------------------------------- the cards */

/**
 * The line of facets of §15: « 5 pages · Entretien fondateur · Claire
 * empathique ».
 *
 * Three parts, ALWAYS three: unlike the protocol's jump count, a missing
 * protocol and a missing persona are facts an operator looks for - « lesquelles
 * n'ont pas encore de protocole » is the question this line answers at a
 * glance. The mockup words both in lower case, « comme des valeurs », which is
 * also what keeps them from being read as names.
 *
 * The parts come back as a list and not as one joined string: the card draws
 * the middot in its own JSX, for the measurement reason `versioned-card.tsx`
 * spells out at its `Facet` component.
 */
export function experienceFacets(card: ExperienceCard): string[] {
  return [
    countLabel(card.pageCount, 'page'),
    card.protocolName ?? 'sans protocole',
    card.personaName ?? 'sans persona',
  ];
}

/**
 * « Joué par 2 liens », or NOTHING when the count is nul (§4, §15, H25).
 *
 * An experience's referents are not versioned objects but the ACTIVE LINKS of
 * §5.1, which is why this sentence is the experience's own rather than
 * `usageSentence`: `versionedUsageCount` knows four kinds and `link` is not one
 * of them, so `card.usage` is empty for every experience and would word
 * nothing.
 *
 * The empty string on zero is the rule of §4 met again - « un usage nul ne
 * s'affiche pas » - and until the Partage lot (issue #59) posts the links
 * table, zero is the honest answer for every experience.
 */
export function linkUsageSentence(linkCount: number): string {
  return linkCount === 0 ? '' : `Joué par ${countLabel(linkCount, 'lien')}`;
}

/**
 * The warning line of a card: « Remplacer le persona « Voix neutre », retiré le
 * 8 sept. 2026. » (§4, §15).
 *
 * A LINE and not an encart (mockup README): « un encart dans une carte pèserait
 * plus que la carte ». The date is `versionedDay` and therefore carries its
 * year where the mockup's artboard did not - one spelling of a date in the
 * whole application is worth more than four characters on a card.
 *
 * Both nouns are masculine, so one sentence serves both kinds.
 */
export function retiredReferenceSentence(entry: ExperienceRetiredReference): string {
  const noun = entry.kind === 'protocol' ? 'le protocole' : 'le persona';
  return `Remplacer ${noun} « ${entry.name} », retiré le ${versionedDay(entry.retiredAt)}.`;
}

/* -------------------------------------------------------------- the filters */

/** « Lien actif » (§15), the third own filter - a yes/no over the links of §5.1. */
export const LINK_FILTER_LABEL: Readonly<Record<ExperienceLinkFilter, string>> = {
  all: 'Tous',
  yes: 'Avec un lien actif',
  no: 'Sans lien actif',
};

/* ------------------------------------------------- the pages of the conversation */

/**
 * What the editor SAYS of a page played in the conversation (prolongation.md §3,
 * §6; issue #186; planche `editeur.html`).
 *
 * One spelling for the five places that say it - the box of the « Page » panel,
 * the pastille of the Timeline, the edge, the stack and the preview - and for the
 * refusals of the save box below, which name the box by its label.
 */
export const CONVERSATION_LABEL = 'Conversation';
export const CONVERSATION_BOX_LABEL = 'Jouer dans la conversation';
export const CONVERSATION_BOX_HELP =
  'L’interviewer amène la page dans le fil du protocole ; la personne ne fait que cliquer.';
export const CONVERSATION_CONTEXT_LABEL = 'Contexte servi à l’interviewer';
export const CONVERSATION_CONTEXT_HELP =
  'Ce que l’interviewer doit savoir pour amener la page. Il ne voit aucun bloc : ni les cartes, ni les libellés. @ ou le bouton { } insèrent une variable.';
export const CONVERSATION_NO_TIMING =
  'Pas de durée automatique ni de transition : la page avance au clic, dans le fil.';
/** The last page of the thread keeps its transition, and loses only the duration (§3.3). */
export const CONVERSATION_NO_DURATION =
  'Pas de durée automatique : la page avance au clic, dans le fil.';
export const CONVERSATION_TRANSITION_OUT = 'La transition joue du fil vers la page suivante.';

/** What the edge between two pages of the thread says instead of the transition (§6). */
export const IN_THREAD_LABEL = 'dans le fil';
/** What a Texte, an Image or a Son of a page of the thread wears in the stack (§3.3, §6). */
export const HIDDEN_IN_THREAD_LABEL = 'masqué dans le fil';
/** What the separator of a temps says on a page of the thread (§3.3, §6). */
export const TEMPS_IGNORED_LABEL = 'ignoré dans le fil';

/**
 * Why the box is greyed on a page that cannot take it (§3.1) - one sentence per
 * reason of `conversationEligibilityOf`, the mockup's for the page before the
 * protocol.
 */
export function conversationReasonSentence(reason: ConversationIneligibleReason): string {
  switch (reason) {
    case 'protocol-page':
      return 'La page protocole est celle où la conversation se joue : elle ne se joue pas dans le fil.';
    case 'feedback-page':
      return 'La page de retour ne se joue jamais dans la conversation : elle s’envoie sur une page.';
    case 'no-protocol-page':
      return 'Cette expérience n’a pas de page protocole : il n’y a pas de conversation à prolonger.';
    case 'before-protocol':
      return 'Cette page vient avant la page protocole. Seules les pages qui suivent directement la page protocole, ou une page déjà jouée dans la conversation, peuvent s’y jouer.';
    case 'after-ordinary-page':
      return 'Une page ordinaire s’intercale entre la page protocole et celle-ci. Seules les pages qui suivent directement la page protocole, ou une page déjà jouée dans la conversation, peuvent s’y jouer.';
    case 'unknown-page':
      return 'Cette page n’est plus dans la timeline de l’expérience.';
    default:
      return assertNever(reason, 'conversationReasonSentence');
  }
}

/**
 * The title of the bubble that stands where the interviewer's would be in a
 * preview (§6): « Jeanne amène la page », « L'interviewer amène la page » when
 * the persona is anonymous or not chosen.
 */
export function interviewerBringsSentence(firstName: string | null): string {
  return `${firstName === null ? 'L’interviewer' : firstName} amène la page`;
}

/** The fixed witness bubble that opens the thread of a preview (§6): never written by a model. */
export const CONVERSATION_WITNESS_BUBBLE = 'Merci, c’était un beau souvenir à partager.';

/** What the dashed bubble holds when the page has no context yet. */
export const CONVERSATION_CONTEXT_EMPTY_BUBBLE =
  'Aucun contexte : l’interviewer amènera la page sans indication.';

/* ------------------------------------------------------------- the refusals */

/**
 * The current NAME of a variable, or null when the screen cannot resolve it -
 * what a refused line names the variable by (§8.6, grill Variables D3).
 *
 * A lookup and not a list, because the answer needs the timeline AND the
 * protocol the protocol page plays: `variableNamer` of `lib/variable-pill.ts`
 * builds one from the screen's sources, and a box mounted without it falls
 * back on the stored token.
 */
export type VariableNamer = (variable: ExperienceVariable) => string | null;

/**
 * The TYPE of a block as the screen names it (« Image », « Texte »), or null
 * when the screen cannot find the block - what a refused line of a display
 * condition names its block by (§9.2; maquette `EnregistrerVersion`: « La
 * condition du bloc Image, page 5, … »). A lookup, like `VariableNamer`, because
 * the problem carries the block's id and only the screen holds the content.
 */
export type BlockNamer = (blockId: string) => string | null;

/**
 * WHERE the « Ouvrir » of a refusal line points (mockup `EnregistrerVersion`).
 *
 * A `{ tab, page }` and never a URL: the layer is blind to the route it sits
 * on, and a `<Link>` written in a pure module could not typecheck against a
 * route tree. The editor turns this into `?tab=timeline&page=<id>`.
 *
 * `protocol-without-persona` is the one problem that does not sit on a page -
 * it is a fact about the experience - so it points at the Persona tab, which is
 * where the fix is (§10.2).
 */
export type ExperiencePlace = {
  readonly tab: 'timeline' | 'persona';
  readonly pageId: string | null;
};

export function problemPlace(problem: ExperienceProblem): ExperiencePlace {
  if (problem.kind === 'protocol-without-persona') return { tab: 'persona', pageId: null };
  return { tab: 'timeline', pageId: problem.pageId };
}

/**
 * « page 2, Consentement et identité » - ONE spelling of a page, for the red
 * encart, the `@` palette and the fiche of a variable alike: an operator reads
 * a page in the box and looks for the same words in the palette.
 */
export function pageWhere(pageIndex: number, pageName: string | null): string {
  const name = pageName === null || pageName === '' ? '' : `, ${pageName}`;
  return `page ${pageIndex + 1}${name}`;
}

/** « (page 2, Consentement et identité) » - where a problem sits, or the empty string. */
function problemWhere(problem: ExperienceProblem): string {
  if (problem.kind === 'protocol-without-persona') return '';
  if (problem.pageIndex === null) return '';
  return ` (${pageWhere(problem.pageIndex, problem.pageName)})`;
}

/**
 * « La page « Le mail » (page 7) » - the page a conversation refusal names, at the
 * head of its line (prolongation.md §3.4). The name alone, the rank alone or
 * neither: a page nobody has named is still a page the operator has to find.
 */
function namedPage(place: {
  readonly pageName: string | null;
  readonly pageIndex: number | null;
}): string {
  const named = place.pageName === null || place.pageName === '' ? null : place.pageName;
  const rank = place.pageIndex === null ? null : place.pageIndex + 1;
  if (named !== null && rank !== null) return `La page « ${named} » (page ${rank})`;
  if (named !== null) return `La page « ${named} »`;
  if (rank !== null) return `La page ${rank}`;
  return 'Une page';
}

type VariableProblem = Extract<
  ExperienceProblem,
  { kind: 'unresolved-variable' | 'variable-out-of-scope' }
>;

/** « @email », or the stored token when nothing names it. */
function variableLabel(problem: VariableProblem, nameOf: VariableNamer | undefined): string {
  const name = nameOf?.({ kind: problem.variableKind, id: problem.variableId }) ?? null;
  return name === null ? problem.token : `@${name}`;
}

/**
 * « @email », or « La variable {{champ:…}} » when nothing names it - how a
 * refused line calls the variable it is about, at the head of the line.
 */
function problemVariable(problem: VariableProblem, nameOf: VariableNamer | undefined): string {
  const label = variableLabel(problem, nameOf);
  return label === problem.token ? `La variable ${problem.token}` : label;
}

/** « page 4, Deux textes » for a page a noyau refusal measures against, or the empty string. */
function referenceWhere(page: { pageIndex: number | null; pageName: string | null }): string {
  return page.pageIndex === null ? '' : pageWhere(page.pageIndex, page.pageName);
}

/** What needs the retained noyau, at the head of a `noyau-first-choice-missing` line. */
const FIRST_CHOICE_NEEDED: Readonly<
  Record<Extract<ExperienceProblem, { kind: 'noyau-first-choice-missing' }>['needs'], string>
> = {
  'second-choice': 'Un second choix est posé',
  portrait: 'Un portrait est posé',
  variable: '@noyau est cité',
  condition: 'Une condition vise @noyau',
};

/** The other way out of a page of choice, at the head of a `noyau-choice-page-exit` line. */
const OTHER_EXIT: Readonly<
  Record<Extract<ExperienceProblem, { kind: 'noyau-choice-page-exit' }>['exit'], string>
> = {
  choice: 'Un second choix est posé',
  'next-page-button': 'Un Bouton qui change de page est posé',
  'auto-duration': 'Une durée automatique est réglée',
};

/** What leaves the feedback page, at the head of a `feedback-page-exit` line (§7.3). */
const FEEDBACK_EXIT: Readonly<
  Record<Extract<ExperienceProblem, { kind: 'feedback-page-exit' }>['exit'], string>
> = {
  'next-page-button': 'Un Bouton qui change de page est posé',
  'auto-duration': 'Une durée automatique est réglée',
};

/** What is wrong with the options of a Sélection, at the head of a `selection-options` line (§7.3). */
const SELECTION_OPTIONS: Readonly<
  Record<Extract<ExperienceProblem, { kind: 'selection-options' }>['reason'], string>
> = {
  'too-few': 'Une Sélection a moins de deux options',
  'too-many': 'Une Sélection a plus de cinq options',
  'empty-option': 'Une option d’une Sélection est vide',
  'duplicate-option': 'Deux options d’une Sélection portent le même libellé',
};

/** « pages 1 et 3 » - the two pages of a repeated one, the kept one first. */
function twoPages(
  first: { pageIndex: number | null },
  problem: { pageIndex: number | null },
): string {
  if (first.pageIndex === null || problem.pageIndex === null) return 'dans la timeline';
  if (first.pageIndex === problem.pageIndex) return `page ${problem.pageIndex + 1}`;
  return `pages ${first.pageIndex + 1} et ${problem.pageIndex + 1}`;
}

/** « La condition du bloc Image » - or « d'un bloc » when the screen cannot name it. */
function conditionSubject(problem: VariableProblem, blockName: BlockNamer | undefined): string {
  const name = problem.blockId === null ? null : (blockName?.(problem.blockId) ?? null);
  return name === null ? 'La condition d’un bloc' : `La condition du bloc ${name}`;
}

/**
 * One refused line of the red encart (§9.1).
 *
 * `nameOf` is what lets a line say « @email » instead of « La variable
 * {{champ:…}} » (D3): the token is what the operator would search the editor
 * for, the name is what they read on the pill. Absent, the token stands.
 * `blockName` does the same for the block a display condition sits on
 * (« La condition du bloc Image »); absent, « d'un bloc ».
 *
 * The refusals of the noyau (noyau.md §3.4) and of the display conditions
 * (§9.2 of the refonte) are worded in the words of the maquette
 * `EnregistrerVersion` where it gives them.
 */
export function problemSentence(
  problem: ExperienceProblem,
  nameOf?: VariableNamer,
  blockName?: BlockNamer,
): string {
  switch (problem.kind) {
    case 'protocol-without-persona':
      return 'La page protocole porte un protocole, mais aucun persona n’est choisi';
    case 'unresolved-variable':
      return problem.inCondition === true
        ? `${conditionSubject(problem, blockName)} cite ${variableLabel(problem, nameOf)}, qui ne se résout pas${problemWhere(problem)}`
        : `${problemVariable(problem, nameOf)} ne se résout pas${problemWhere(problem)}`;
    case 'missing-asset':
      return `Un fichier de la médiathèque a été supprimé${problemWhere(problem)}`;
    case 'duplicate-variable-name':
      return `Deux variables portent le nom « ${problem.name} »${problemWhere(problem)}`;
    case 'variable-out-of-scope':
      return problem.inCondition === true
        ? conditionScopeSentence(
            problem,
            variableLabel(problem, nameOf),
            conditionSubject(problem, blockName),
          )
        : `${problemVariable(problem, nameOf)} ${scopeSentence(problem)}`;
    case 'person-name-repeated':
      return `Deux champs portent « Nom de la personne dans Sessions », ${twoPages(problem.firstPage, problem)} : un seul par expérience`;
    case 'noyau-before-protocol':
      return `Un bloc Noyau vient avant la page protocole${problemWhere(problem)} : la séquence du noyau suit l’entretien`;
    case 'noyau-choice-repeated':
      return `Deux ${problem.mode === 'first-choice' ? 'premiers choix' : 'seconds choix'}, ${twoPages(problem.firstPage, problem)} : un seul par expérience`;
    case 'noyau-first-choice-missing':
      return `${FIRST_CHOICE_NEEDED[problem.needs]}${problemWhere(problem)}, mais aucun premier choix ne donne le noyau retenu`;
    case 'noyau-out-of-order': {
      const what = problem.mode === 'second-choice' ? 'Le second choix' : 'Le portrait';
      const first = referenceWhere(problem.firstChoicePage);
      const here = problem.pageIndex === null ? '' : pageWhere(problem.pageIndex, problem.pageName);
      return problem.pageIndex !== null && problem.pageIndex === problem.firstChoicePage.pageIndex
        ? `${what} est sur la page du premier choix, ${here} : déplacez-le après`
        : `${what} est ${here}, avant le premier choix${first === '' ? '' : `, ${first}`} : déplacez-le après`;
    }
    case 'noyau-choice-page-exit':
      return `${OTHER_EXIT[problem.exit]} à côté d’un choix${problemWhere(problem)} : le choix doit être la seule sortie`;
    case 'noyau-choice-last-page':
      return `Un choix est posé sur la dernière page${problemWhere(problem)} : il passe à la page suivante, ajoutez une page après lui`;
    case 'condition-on-exit':
      return `${problem.blockType === 'button' ? 'Un Bouton' : 'Un choix du noyau'} porte une condition${problemWhere(problem)} : la sortie d’une page ne se masque pas`;
    case 'feedback-repeated':
      return `Deux pages de retour, ${twoPages(problem.firstPage, problem)} : une seule par expérience`;
    case 'feedback-not-last':
      return `La page de retour n’est pas la dernière de la timeline${problemWhere(problem)} : rien ne vient après elle`;
    case 'feedback-submit-missing':
      return problem.submitCount === 0
        ? `La page de retour n’a aucun Bouton « Envoyer le retour »${problemWhere(problem)} : elle en porte un seul`
        : `La page de retour porte ${problem.submitCount} Boutons « Envoyer le retour »${problemWhere(problem)} : un seul suffit`;
    case 'submit-outside-feedback':
      return `Un Bouton « Envoyer le retour » est posé hors de la page de retour${problemWhere(problem)} : cette action n’existe que sur elle`;
    case 'feedback-page-exit':
      return `${FEEDBACK_EXIT[problem.exit]} sur la page de retour${problemWhere(problem)} : elle ne se quitte pas, elle s’envoie`;
    case 'feedback-thanks-empty':
      return `La phrase de remerciement de la page de retour est vide${problemWhere(problem)}`;
    // The six refusals of a page played in the conversation (prolongation.md §3.4, #186):
    // the page is NAMED and the way out is said, in the manner of the feedback page's lines.
    case 'conversation-page-out-of-run':
      return `${namedPage(problem)} se joue dans la conversation mais ne suit pas directement la page protocole : décochez « ${CONVERSATION_BOX_LABEL} », ou replacez la page`;
    case 'conversation-page-input':
      return `${namedPage(problem)} se joue dans la conversation et porte un champ de saisie : dans le fil, la personne ne tape rien`;
    case 'conversation-page-auto-duration':
      return `${namedPage(problem)} se joue dans la conversation et porte une durée automatique : elle avance au clic, jamais seule`;
    case 'conversation-page-next-temps':
      return `${namedPage(problem)} se joue dans la conversation et porte un Bouton « temps suivant » : le fil n’a pas de temps`;
    case 'conversation-page-no-exit':
      return `${namedPage(problem)} se joue dans la conversation sans aucune sortie à cliquer : ajoutez un Bouton « page suivante » ou un choix du noyau - seule la dernière page de l’expérience peut s’en passer`;
    case 'conversation-page-feedback':
      return `${namedPage(problem)} n’est pas une page de contenu et se joue pourtant dans la conversation : seule une page de contenu s’y joue, décochez « ${CONVERSATION_BOX_LABEL} »`;
    case 'selection-options':
      return `${SELECTION_OPTIONS[problem.reason]}${problemWhere(problem)} : de deux à cinq options, toutes écrites et toutes différentes`;
    default:
      return assertNever(problem, 'problemSentence');
  }
}

/**
 * « La condition du bloc Image, page 5, cite @me recontacter, qui est saisi
 * page 5 : une condition ne cite qu’une variable des pages d’avant » - the
 * maquette's line for a condition out of reach (§9.2 of the refonte).
 */
function conditionScopeSentence(
  problem: Extract<ExperienceProblem, { kind: 'variable-out-of-scope' }>,
  label: string,
  subject: string,
): string {
  const where =
    problem.pageIndex === null ? '' : `, ${pageWhere(problem.pageIndex, problem.pageName)},`;
  const declared =
    problem.declaredPageIndex === null
      ? ''
      : pageWhere(problem.declaredPageIndex, problem.declaredPageName);
  const why =
    problem.reason === 'before-protocol'
      ? `, avant la page protocole${declared === '' ? '' : ` (${declared})`}`
      : problem.variableKind === 'noyau'
        ? `, qui n’existe qu’après le premier choix${declared === '' ? '' : `, ${declared}`}`
        : declared === ''
          ? ', saisi plus loin'
          : `, qui est saisi ${declared}`;
  return `${subject}${where} cite ${label}${why} : une condition ne cite qu’une variable des pages d’avant`;
}

/**
 * The body of an out-of-scope line, which names the TWO pages (grill Variables,
 * D3, mockup planche 4): « est cité page 1, Consentement et identité, mais
 * saisi page 4, Coordonnées ». The masculine agrees with the pill, « @email »,
 * and not with « la variable ».
 */
function scopeSentence(
  problem: Extract<ExperienceProblem, { kind: 'variable-out-of-scope' }>,
): string {
  const cited =
    problem.pageIndex === null
      ? 'est cité hors des pages'
      : `est cité ${pageWhere(problem.pageIndex, problem.pageName)}`;
  const declared =
    problem.declaredPageIndex === null
      ? ''
      : ` (${pageWhere(problem.declaredPageIndex, problem.declaredPageName)})`;
  // `@noyau` is not TYPED on a page, it is GIVEN by the first choice - the
  // maquette's line: « @noyau est cité page 2, mais le premier choix est page 4 :
  // il n’existe qu’à partir de la page qui suit ».
  if (problem.variableKind === 'noyau') {
    return problem.reason === 'same-page' || problem.declaredPageIndex === null
      ? `${cited}, la page même du premier choix : il n’existe qu’à partir de la page qui suit`
      : `${cited}, mais le premier choix est ${pageWhere(problem.declaredPageIndex, problem.declaredPageName)} : il n’existe qu’à partir de la page qui suit`;
  }
  switch (problem.reason) {
    case 'same-page':
      return `${cited}, la page même qui le saisit : la valeur n’existe pas tant que la page s’affiche`;
    case 'before-declaration':
      return `${cited}, mais saisi ${
        problem.declaredPageIndex === null
          ? 'plus loin'
          : pageWhere(problem.declaredPageIndex, problem.declaredPageName)
      }`;
    case 'before-protocol':
      return `${cited}, avant la page protocole${declared} : un objectif n’a de matière qu’après elle`;
    default:
      return assertNever(problem.reason, 'scopeSentence');
  }
}

/**
 * How a referenced object is named in a blocker's sentence. The texts of the
 * noyaux are named by what they are, a PLURAL - « Les textes des noyaux » -
 * which is why the sentences below conjugate by the reference (#151).
 */
function blockerSubject(blocker: ExperienceProductionBlocker): string {
  if (blocker.kind === 'empty-protocol-page') return '';
  if (blocker.reference === 'noyau-texts') return 'Les textes des noyaux';
  const noun = blocker.reference === 'protocol' ? 'Le protocole' : 'Le persona';
  return blocker.name === '' ? noun : `${noun} « ${blocker.name} »`;
}

/** Whether a reference is the texts of the noyaux - the one plural subject. */
function aboutTheTexts(blocker: ExperienceProductionBlocker): boolean {
  return blocker.kind !== 'empty-protocol-page' && blocker.reference === 'noyau-texts';
}

/** The blocker of §8.6 of the refonte - a direction of the protocol that cites an objective gone (#158). */
export type OrphanDirectionBlocker = Extract<
  ExperienceProductionBlocker,
  { kind: 'protocol-variable-orphan' }
>;

/**
 * « Le protocole « Entretien fondateur » (production v5) cite, dans la
 * direction de l'étape « Émergence », un objectif qui n'existe plus » - the fact
 * of §8.6 of the refonte (issue #158, the maquette `LectureVariablesBoiteExperience`
 * word for word), which the yellow and the red lines both open on: capitalised
 * when it opens the sentence, lower case after « Passer en production est
 * refusé : » (`orphanDirectionRefusalSentence`).
 */
function orphanDirectionFact(orphan: OrphanDirectionBlocker, protocol: string): string {
  const step =
    orphan.stepName.trim() === ''
      ? `l’étape ${orphan.stepIndex + 1}`
      : `l’étape « ${orphan.stepName} »`;
  return `${protocol} (${pinResolvedWord(orphan.pin, orphan.versionNumber)}) cite, dans la direction de ${step}, un objectif qui n’existe plus`;
}

/** One refused line of « Passer en production » (§4, §9.1). */
export function blockerSentence(blocker: ExperienceProductionBlocker): string {
  const texts = aboutTheTexts(blocker);
  switch (blocker.kind) {
    case 'reference-without-production':
      return `${blockerSubject(blocker)} ${texts ? 'n’ont' : 'n’a'} aucune version en production`;
    case 'reference-retired':
      return `${blockerSubject(blocker)} ${texts ? 'sont retirés' : 'est retiré'} depuis le ${versionedDay(blocker.retiredAt)}`;
    case 'reference-unknown':
      // The texts are born on their first version (N8): before it they are not
      // gone, they have never been (noyau.md §6.4).
      return texts
        ? `${blockerSubject(blocker)} n’ont encore aucune version`
        : `${blockerSubject(blocker)} n’existe plus`;
    case 'empty-protocol-page':
      return `La page protocole « ${blocker.pageName} » est vide (page ${blocker.pageIndex + 1})`;
    case 'analyst-levels-missing':
      // analyste.md §3.6 (#196): one line names every level no objective carries.
      return `${blockerSubject(blocker)} (${pinResolvedWord(blocker.pin, blocker.versionNumber)}) n’a aucun objectif ${analystLevelsPhrase(blocker.levels)}`;
    case 'analyst-reading-missing':
      // analyste.md §3.6 (#196): one line per PASS, which the line names - a protocol with
      // both readings empty says it twice, and the two lines must not read alike.
      return `${blockerSubject(blocker)} (${pinResolvedWord(blocker.pin, blocker.versionNumber)}) n’a pas de lecture pour la passe ${blocker.pass} de l’analyste`;
    case 'protocol-variable-orphan':
      return orphanDirectionFact(blocker, blockerSubject(blocker));
    default:
      return assertNever(blocker, 'blockerSentence');
  }
}

/** The blocker of analyste.md §3.6 - a protocol with no reading for one pass of the analyst at its tag. */
export type AnalystReadingBlocker = Extract<
  ExperienceProductionBlocker,
  { kind: 'analyst-reading-missing' }
>;

/** The blocker of analyste.md §3.6 (#196) - a protocol with a level no objective carries at its tag. */
export type AnalystLevelsBlocker = Extract<
  ExperienceProductionBlocker,
  { kind: 'analyst-levels-missing' }
>;

/**
 * The RED line of the save box for `analyst-reading-missing` (noyau.md §3.7,
 * analyste.md §3.6): « Passer en production est refusé : le protocole « PCQ »
 * (production v11) n'a pas de lecture pour la passe 2 de l'analyste ».
 *
 * It opens on the gesture refused, where `blockerSentence` names the object
 * alone: the red encart's title says « La version ne peut pas être
 * enregistrée », and this fact refuses the TICK only - untick it and the
 * version saves, with the yellow twin of this line. `blockerSentence` keeps
 * its own wording for the 409, which already opens on « Refusé en production ».
 */
export function analystReadingRefusalSentence(blocker: AnalystReadingBlocker): string {
  const protocol = blocker.name === '' ? 'le protocole' : `le protocole « ${blocker.name} »`;
  return `Passer en production est refusé : ${protocol} (${pinResolvedWord(blocker.pin, blocker.versionNumber)}) n’a pas de lecture pour la passe ${blocker.pass} de l’analyste`;
}

/**
 * The RED line of the save box for `analyst-levels-missing` (analyste.md §3.6,
 * #196), the twin of the one above and opened the same way: « Passer en production
 * est refusé : le protocole « Scénario de succès » (production v17) n'a aucun
 * objectif au niveau Comment ».
 */
export function analystLevelsRefusalSentence(blocker: AnalystLevelsBlocker): string {
  const protocol = blocker.name === '' ? 'le protocole' : `le protocole « ${blocker.name} »`;
  return `Passer en production est refusé : ${protocol} (${pinResolvedWord(blocker.pin, blocker.versionNumber)}) n’a aucun objectif ${analystLevelsPhrase(blocker.levels)}`;
}

/**
 * The RED line of the save box for `protocol-variable-orphan` (§8.6 of the
 * refonte, the maquette `LectureVariablesBoiteExperience` word for word; issue
 * #158): « Passer en production est refusé : le protocole « Entretien
 * fondateur » (production v5) cite, dans la direction de l'étape « Émergence »,
 * un objectif qui n'existe plus » - opened on the gesture refused, for the
 * reason `analystReadingRefusalSentence` gives.
 */
export function orphanDirectionRefusalSentence(blocker: OrphanDirectionBlocker): string {
  const protocol = blocker.name === '' ? 'le protocole' : `le protocole « ${blocker.name} »`;
  return `Passer en production est refusé : ${orphanDirectionFact(blocker, protocol)}`;
}

/**
 * What the yellow encart keeps beside the red one: a fact the refusal already
 * says is said ONCE (noyau.md §3.7, « case cochée, l'avertissement jaune se
 * retire devant le refus »).
 *
 * The dry run answers `analyst-reading-missing` as a warning whether the box
 * is ticked or not - the request asks the superset (`experience-editor.tsx`) -
 * so it is the box that withdraws the yellow line of a protocol the red
 * encart already names: one line per PASS since #196, withdrawn by the red line of
 * the same protocol and the same pass, and the line of the levels
 * (`analyst-levels-missing`, #196), withdrawn by the red one of the same protocol.
 * And since #158 `protocol-variable-orphan`, one line per STEP, withdrawn by the
 * red line of the same protocol and the same step (§8.6 of the refonte). Only
 * those three kinds: the other warnings of the box are facts the refusals do not
 * repeat word for word, and they stay as they were.
 */
export function warningsBesideBlockers(
  warnings: readonly ExperienceWarning[],
  blockers: readonly ExperienceProductionBlocker[],
): ExperienceWarning[] {
  return warnings.filter((warning) => {
    if (warning.kind === 'analyst-levels-missing') {
      return !blockers.some(
        (blocker) => blocker.kind === 'analyst-levels-missing' && blocker.id === warning.id,
      );
    }
    if (warning.kind === 'analyst-reading-missing') {
      return !blockers.some(
        (blocker) =>
          blocker.kind === 'analyst-reading-missing' &&
          blocker.id === warning.id &&
          blocker.pass === warning.pass,
      );
    }
    if (warning.kind === 'protocol-variable-orphan') {
      return !blockers.some(
        (blocker) =>
          blocker.kind === 'protocol-variable-orphan' &&
          blocker.id === warning.id &&
          blocker.stepId === warning.stepId,
      );
    }
    return true;
  });
}

/**
 * One line of the YELLOW encart - an avertissement and never a refusal (§9.1).
 *
 * « le persona Claire empathique n'a jamais été passé en production : un lien
 * en développement échouera au démarrage tant que ce sera le cas ». The version
 * saves and plays in `développement`; it is the REFERENT that says what is left
 * to do, which is why the sentence names it and says what it will cost.
 *
 * The three lines of the noyau (noyau.md §3.4, §3.5; #151): the texts that
 * have nothing in production (the maquette `EnregistrerVersion`, word for
 * word), the texts that have no version at all (§6.4), and the empty protocol
 * page that will always skip the sequence.
 */
export function warningSentence(warning: ExperienceWarning): string {
  switch (warning.kind) {
    case 'noyau-texts-missing':
      return 'Les textes des noyaux n’ont encore aucune version : aucune session de cette expérience ne démarre tant qu’ils ne sont pas enregistrés dans Réglages > Noyaux';
    case 'noyau-sequence-skipped':
      return `La page protocole « ${warning.pageName} » est vide (page ${warning.pageIndex + 1}) : la séquence du noyau sera toujours sautée`;
    case 'conversation-context-empty':
      return `${namedPage(warning)} se joue dans la conversation sans contexte : l’interviewer amènera la page sans indication`;
    case 'analyst-levels-missing':
      // analyste.md §3.6 (#196): the line of the levels, the twin of the reading's below.
      return `${blockerSubject(warning)} (${pinResolvedWord(warning.pin, warning.versionNumber)}) n’a aucun objectif ${analystLevelsPhrase(warning.levels)} : aucune session de cette expérience ne démarrera tant que chaque niveau n’en aura pas un`;
    case 'analyst-reading-missing':
      // analyste.md §3.6, the example of the spec word for word: the pass is named, so a
      // protocol with both readings empty does not say the same line twice.
      return `${blockerSubject(warning)} (${pinResolvedWord(warning.pin, warning.versionNumber)}) n’a pas de lecture pour la passe ${warning.pass} de l’analyste : aucune session de cette expérience ne démarrera tant qu’il n’en aura pas`;
    case 'protocol-variable-orphan':
      // §8.6 of the refonte (#158), the maquette `LectureVariablesBoiteExperience` word for word.
      return `${orphanDirectionFact(warning, blockerSubject(warning))} : « Passer en production » sera refusé tant que ce sera le cas`;
    default:
      return assertNever(warning, 'warningSentence');
  }
}

/**
 * The experience's OWN wording of a 409, or `null` when the body carries none.
 *
 * The twin of `protocolRefusalSentence`, and it exists for the same reason:
 * `invalid-timeline` and `production-unresolved` are states of the CONTENT and
 * deliberately NOT among the six codes of `versionedErrorSchema`, so
 * `versionedRefusalOf` answers null on them and the generic sentence - which
 * names nothing - would have won. `versioned-editor` tries this one FIRST
 * through its `ownRefusal` slot and falls back to the six.
 *
 * A 409 should never reach an operator here: the save box reads
 * `POST /api/experiences/check` before the click and greys the button. This
 * sentence is the guard behind that courtesy - a draft that changed between the
 * dry run and the post, or a file somebody deleted in the meantime.
 */
export function experienceRefusalSentence(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const parsed = experienceErrorSchema.safeParse(error.details);
  if (!parsed.success) return null;
  const refusal = parsed.data;
  switch (refusal.code) {
    case 'invalid-timeline': {
      const sentences = refusal.problems.map((problem) => problemSentence(problem)).join(' ; ');
      const count = countLabel(refusal.problems.length, 'problème');
      return sentences === ''
        ? `L’enregistrement est refusé : ${count}.`
        : `L’enregistrement est refusé : ${count}. ${sentences}.`;
    }
    case 'production-unresolved': {
      const sentences = refusal.blockers.map(blockerSentence).join(' ; ');
      return `Refusé en production : ${sentences}.`;
    }
    default:
      return assertNever(refusal.code, 'experienceRefusalSentence');
  }
}
